import { Command, Option } from "clipanion";
import { DefaultAzureCredential } from "@azure/identity";
import { platformTag } from "@m8t-stack/platform-release";
import { M8tCommand } from "../../lib/m8t-command.js";
import { getAzAccount, getCallerObjectId } from "../../lib/auth.js";
import { LocalCliError } from "../../lib/errors.js";
import { resolveGatewayContext } from "../../lib/gateway-context.js";
import { adoptLegacyGateway } from "../../lib/gateway-adoption.js";
import { compareSemver, parseContainerAppResourceId } from "../../lib/platform-update.js";
import { fetchManifest } from "../../lib/release-channel.js";
import { renderJson, resolveOutputMode } from "../../lib/output.js";
import { readStampOutcome } from "../../lib/platform-stamp.js";

export class PlatformGatewayAdoptCommand extends M8tCommand {
  static paths = [["platform", "gateway", "adopt"]];
  static usage = Command.Usage({
    description: "Explicitly authorize one legacy m8t ACR gateway for managed, digest-pinned updates.",
    details:
      "Fail-closed ownership transition. Reads the live gateway and ACR digest, requires the operator to repeat both exactly, verifies the generated legacy topology, and atomically writes the adoption marker with a pending platform update. Unadopted BYOC remains external.",
  });

  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");
  target = Option.String("--target", { description: "Published platform target whose installer contains adoption support." });
  expectedImage = Option.String("--expected-image", { description: "Exact live legacy ACR image ref." });
  expectedDigest = Option.String("--expected-digest", { description: "Exact sha256 digest resolved from that ACR image." });
  expectedAcrResourceId = Option.String("--expected-acr-resource-id", { description: "Exact ARM id of the legacy ACR." });
  output = Option.String("--output");

  async executeCommand(): Promise<number> {
    const need = (v: unknown, flag: string): string => {
      if (typeof v !== "string" || v.trim() === "") throw new LocalCliError({ code: "PLATFORM_GATEWAY_ADOPTION_ARG_MISSING", message: `${flag} is required.` });
      return v.trim();
    };
    const mode = resolveOutputMode(this.output as "pretty" | "json" | "auto" | undefined, this.context.stdout as NodeJS.WriteStream);
    const account = await getAzAccount();
    const subscriptionId = need(this.subscription ?? account.subscriptionId, "--subscription");
    const gw = await resolveGatewayContext({ subscriptionId, resourceGroup: this.resourceGroup, interactive: mode !== "json" });
    const parsed = parseContainerAppResourceId(gw.containerAppResourceId);
    const resourceGroup = need(this.resourceGroup ?? parsed.resourceGroup, "--resource-group");
    const target = need(this.target, "--target");
    const manifest = await fetchManifest({ channel: true, version: target });
    const assertTarget = (requested: string, actual: typeof manifest): void => {
      const normalized = requested.replace(/^platform-v/, "");
      if (actual.platform.version.replace(/^platform-v/, "") !== normalized || actual.platform.tag !== platformTag(normalized)) {
        throw new LocalCliError({
          code: "PLATFORM_GATEWAY_ADOPTION_TARGET_MISMATCH",
          message: `Requested target ${requested}, but the release service returned ${actual.platform.version}.`,
        });
      }
    };
    assertTarget(target, manifest);
    if (
      compareSemver(`v${manifest.components.installer.version}`, "v0.1.76") < 0 ||
      compareSemver(`v${manifest.components.cli.recommended}`, "v0.2.109") < 0
    ) {
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_ADOPTION_TARGET_TOO_OLD",
        message: `Platform target ${target} predates digest-bound legacy gateway adoption support.`,
        hint: "Choose a published platform target whose installer is at least 0.1.76 and recommended CLI is at least 0.2.109.",
      });
    }
    const credential = new DefaultAzureCredential();
    const installed = await readStampOutcome({ credential, subscriptionId, resourceGroup });
    if (installed.source !== "explicit") {
      throw new LocalCliError({
        code: "PLATFORM_GATEWAY_ADOPTION_NO_ROLLBACK",
        message: "The installed platform stamp is absent or unreadable; refusing a gateway ownership transition without its exact rollback target.",
      });
    }
    const rollbackVersion = installed.stamp.platformVersion;
    const rollbackManifest = await fetchManifest({ channel: true, version: rollbackVersion });
    assertTarget(rollbackVersion, rollbackManifest);
    const result = await adoptLegacyGateway({
      credential,
      subscriptionId,
      resourceGroup,
      gatewayResourceId: gw.containerAppResourceId,
      target,
      expectedImage: need(this.expectedImage, "--expected-image"),
      expectedDigest: need(this.expectedDigest, "--expected-digest"),
      expectedAcrResourceId: need(this.expectedAcrResourceId, "--expected-acr-resource-id"),
      targetGatewayTag: manifest.components.gateway.tag,
      targetGatewayDigest: manifest.components.gateway.digest,
      rollbackGatewayTag: rollbackManifest.components.gateway.tag,
      rollbackGatewayDigest: rollbackManifest.components.gateway.digest,
      rollbackPlatformVersion: rollbackVersion,
      operatorIdentity: await getCallerObjectId(),
      onProgress: (m) => this.context.stderr.write(`${m}\n`),
    });
    this.context.stdout.write(mode === "json" ? renderJson(result) + "\n" : `${result.outcome}: ${result.image}@${result.digest}\n`);
    return 0;
  }
}
